/**
 * Utility functions for managing Open Graph meta tags dynamically
 */

/**
 * Update Open Graph meta tags in the document head
 * @param {Object} ogTags - Object containing Open Graph tag values
 * @param {string} ogTags.title - og:title value
 * @param {string} ogTags.description - og:description value
 * @param {string} ogTags.image - og:image value
 * @param {string} ogTags.imageWidth - og:image:width value
 * @param {string} ogTags.imageHeight - og:image:height value
 * @param {string} ogTags.url - og:url value
 * @param {string} ogTags.type - og:type value
 * @param {string} ogTags.siteName - og:site_name value
 */
export const updateOpenGraphMetaTags = (ogTags) => {
  if (!ogTags) return;

  // Helper function to update or create meta tag
  const updateMetaTag = (property, content) => {
    if (!content) return; // Skip if no content provided
    
    let meta = document.querySelector(`meta[property="${property}"]`);
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('property', property);
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', content);
  };

  // Update each Open Graph tag if provided
  if (ogTags.title) {
    updateMetaTag('og:title', ogTags.title);
    // Also update the page title
    document.title = ogTags.title;
  }

  if (ogTags.description) {
    updateMetaTag('og:description', ogTags.description);
  }

  if (ogTags.image) {
    updateMetaTag('og:image', ogTags.image);
  }

  if (ogTags.imageWidth) {
    updateMetaTag('og:image:width', ogTags.imageWidth);
  }

  if (ogTags.imageHeight) {
    updateMetaTag('og:image:height', ogTags.imageHeight);
  }

  if (ogTags.url) {
    updateMetaTag('og:url', ogTags.url);
  }

  if (ogTags.type) {
    updateMetaTag('og:type', ogTags.type);
  }

  if (ogTags.siteName) {
    updateMetaTag('og:site_name', ogTags.siteName);
  }
};

/**
 * Reset Open Graph meta tags to default values from index.html
 * This is useful when navigating away from a Learning screen
 */
export const resetOpenGraphMetaTags = () => {
  // Default values from index.html
  const defaults = {
    title: 'Discover Your Optimal Pace -> Thrive Through Anything, Outperform Anyone',
    description: 'Feel successful, stop throwing away money, and gain the cold confidence that comes from knowing you can thrive through anything and outperform anyone. Join 1,614 members, discover your Optimal Pace, and begin your journey to Superhuman Performance.',
    image: 'https://firebasestorage.googleapis.com/v0/b/insatiable-e0f18.firebasestorage.app/o/OG%20Sharing%20Image%20for%20Optimal%20Pace%20Academy%20Membership.png?alt=media&token=b7d5ea7a-8a59-42e2-8fc0-76979a837e0d',
    imageWidth: '1200',
    imageHeight: '630',
    url: 'https://entry.thrivewithdory.com',
    type: 'website',
    siteName: 'Join the Optimal Pace Academy Community'
  };

  updateOpenGraphMetaTags(defaults);
  document.title = defaults.title;
};

